import type { PlanGeoData } from '../proposition-c/types';
import { buildingsByIds, YEARS } from './engine';
import { composition } from './composition';
import { planStatus, staleReasons } from './model';
import { FAMILIES, type Plan, type Project } from './types';

export function routeOf(plan: Plan, pipeCount: number) {
  const ids = plan.buildingIds ?? [];
  const indexes = (plan.routePipeIndexes ?? []).filter((index) => index < pipeCount);
  const phases = Object.fromEntries(indexes.map((index) => {
    const years = ids.slice(index * 2, index * 2 + 2).map((id) => Number(plan.buildingPhases?.[id] ?? YEARS[0]));
    return [index, plan.pipePhaseOverrides?.[index] ?? String(years.length ? Math.min(...years) : Number(YEARS[0]))];
  }));
  return { indexes, phases };
}

export function captureInputs(project: Project, plan: Plan, geo?: PlanGeoData) {
  const { sources, added, excluded } = composition(project, plan);
  const ids = [...new Set(plan.buildingIds ?? [])];
  const indexed = new Map(buildingsByIds(ids).map((building) => [building.id, building]));
  const route = routeOf(plan, geo?.pipes.features.length ?? 0);
  return structuredClone({
    project: {
      id: project.id, name: project.name, owner: project.owner, perimeterLabel: project.perimeterLabel,
      snapshotDate: project.snapshotDate, umDriftCount: project.umDriftCount, params: project.params,
    },
    plan: { id: plan.id, name: plan.name, energy: plan.energy, updatedAt: plan.updatedAt },
    hypotheses: FAMILIES.map(({ key }) => ({
      family: key, selectedId: plan.setIds[key],
      values: project.sets[key].find((set) => set.id === plan.setIds[key]) ?? null,
    })),
    selections: sources,
    missingSelectionIds: (plan.selectionIds ?? []).filter((id) => !sources.some((source) => source.id === id)),
    addedBuildingIds: added,
    excludedBuildingIds: excluded,
    manualOverrides: {
      added: plan.manualBuildingIds ?? added,
      excluded: plan.excludedBuildingIds ?? excluded,
    },
    buildings: ids.map((id) => ({
      id, data: indexed.get(id) ?? null,
      geometry: geo?.buildings.features.find((feature) => feature.properties?.functionalId === id)?.geometry ?? null,
    })),
    trace: {
      mode: plan.routeMode ?? 'auto', ...route,
      segments: route.indexes.map((index) => ({ index, feature: geo?.pipes.features[index] ?? null })),
    },
    technicalPoints: project.technicalPoints ?? [],
    sectionState: {
      buildingsConfirmed: !!plan.buildingsConfirmed, traceCalculated: !!plan.traceCalculated,
      densityCalculated: !!plan.densityCalculated, invalidations: plan.invalidations ?? [],
      densityCorrection: plan.densityCorrection ?? null,
    },
    phasing: {
      mode: plan.phasingMode ?? 'manual',
      buildings: Object.fromEntries(ids.map((id) => [id, plan.buildingPhases?.[id] ?? YEARS[0]])),
      pipePhases: route.phases,
      pipeManualOverrides: plan.pipePhaseOverrides ?? {},
      rule: 'Mock : phase la plus précoce des bâtiments associés, deux bâtiments par tronçon.',
    },
    geography: geo ? {
      source: 'Copie Gentilly — proposition-c', perimeter: geo.perimeter, roads: geo.roads,
      pipes: geo.pipes, deliveryPoints: geo.deliveryPoints, bounds: geo.bounds,
    } : null,
    simulation: {
      engine: 'navigation-projets — modèle illustratif, non métier',
      samplingSkip: project.skip,
      consumptionUnit: 'MWh/an',
      segmentIndexing: 'Indices à partir de zéro dans les données ; numéros à partir de un dans l’interface.',
      limitations: [
        'Calcul et tracé simulés ; aucune garantie de raccordement géographique.',
        'Ratios disponibles sous forme de références de jeux de données, pas de tables numériques dans ce prototype.',
        'Consommations des bâtiments : estimations chaleur de Gentilly ; facteur froid simulé de 0,35.',
        'Les paramètres projet sont documentés ; le moteur simplifié ne les applique pas tous.',
      ],
    },
  });
}

export type PlanInputs = ReturnType<typeof captureInputs>;

export function exportPlan(project: Project, plan: Plan, geo?: PlanGeoData) {
  return {
    format: 'plan2cost-construction-v1',
    exportedAt: new Date().toISOString(),
    currentState: planStatus(project, plan),
    changesSinceCalculation: staleReasons(project, plan),
    currentConfiguration: captureInputs(project, plan, geo),
    lastCalculation: plan.snapshot ? {
      computedAt: plan.snapshot.computedAt,
      inputs: plan.snapshot.inputs ?? null,
      historicalSetVersions: plan.snapshot.sets,
      results: plan.snapshot.results ?? null,
      warning: plan.snapshot.inputs ? null : 'Ancien calcul mock : paramètres complets non archivés. Relancer pour les figer.',
    } : null,
    calculationArchives: plan.archives ?? [],
  };
}
